import React, { Fragment, useEffect, useState } from "react";

import { DeliveryTypes } from "@utils/enums";
import { ProductConfiguration } from "@utils/productConfiguration/productConfiguration.types";

import { CartModel } from "@redux/modules/api/data/cart/@types/cart.model";
import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";

import { PromotionItems } from "@components/ShoppingCart/promotionItems/promotionItems.component";

import ProductGroupBlockComponent from "./productGroupBlock/productGroupBlock.component";
import "./shoppingCartSummary.scss";
import TotalsBlockComponent from "./totalsBlock/totalsBlock.component";

type Props = {
    removeFromCart?: (id: string, quantity: number, free: boolean) => void;
    urlTarget?: string;
    shoppingCart?: CartModel | null;
    closeCallback?: () => void;
    productLinkingAvailable?: boolean;
    storeDetails?: StoreDetailsModel | null | undefined;
    isMiniCart?: boolean;
    isUpdatingCart?: { [key: string]: boolean };
    activeId?: string;
    productConfiguration?: ProductConfiguration;
};

const ShoppingCartSummary = ({
    removeFromCart,
    shoppingCart,
    productLinkingAvailable = false,
    isMiniCart = false,
    storeDetails,
    isUpdatingCart = {},
    activeId,
}: Props) => {
    const [inAnimation, setInAnimation] = useState(false);

    useEffect(() => {
        if (activeId) {
            setTimeout(() => setInAnimation(isUpdatingCart[activeId]), 1400);
        }
    }, [activeId && isUpdatingCart[activeId]]);

    const collectProducts = shoppingCart?.items.filter(
        i =>
            i.deliveryType === DeliveryTypes.ClickAndCollect &&
            !i.isAdditionalService,
    );

    const deliveryProducts = shoppingCart?.items.filter(
        i =>
            i.deliveryType === DeliveryTypes.HomeDelivery &&
            !i.isAdditionalService,
    );

    const deliveryCost = !!deliveryProducts?.length
        ? shoppingCart?.deliveryCost ?? 0
        : undefined;

    return (
        <>
            {!!shoppingCart?.items?.length && (
                <div className={`${isMiniCart && "miniCart"}`}>
                    <Fragment>
                        <section
                            className={`${isMiniCart && "miniCart__overview"}`}
                        >
                            {collectProducts!.length > 0 && (
                                <ProductGroupBlockComponent
                                    title={"general.cart.label.clickandcollect"}
                                    icon={"la-hand-pointer"}
                                    cartItems={collectProducts!}
                                    isClickAndCollect={true}
                                    inAnimation={inAnimation}
                                    isMiniCart={isMiniCart}
                                    activeId={activeId}
                                    productLinkingAvailable={
                                        productLinkingAvailable
                                    }
                                    removeFromCart={removeFromCart}
                                    storeFullName={storeDetails?.fullName}
                                />
                            )}
                            {deliveryProducts!.length > 0 && (
                                <ProductGroupBlockComponent
                                    title={"general.cart.label.delivery"}
                                    icon={"la-truck"}
                                    cartItems={deliveryProducts!}
                                    isClickAndCollect={false}
                                    inAnimation={inAnimation}
                                    isMiniCart={isMiniCart}
                                    activeId={activeId}
                                    productLinkingAvailable={
                                        productLinkingAvailable
                                    }
                                    removeFromCart={removeFromCart}
                                    storeFullName={storeDetails?.fullName}
                                />
                            )}
                        </section>
                        <section>
                            {
                                <TotalsBlockComponent
                                    deliveryCost={deliveryCost}
                                    discountTotal={shoppingCart?.discountTotal}
                                    giftCardTotal={shoppingCart?.giftCardTotal}
                                    serviceTotal={shoppingCart?.serviceTotal}
                                    subTotal={shoppingCart?.subTotal}
                                    totalPrice={shoppingCart?.totalPrice}
                                />
                            }
                        </section>
                        <section>
                            <PromotionItems
                                promotions={shoppingCart?.promotions}
                            />
                        </section>
                    </Fragment>
                </div>
            )}
        </>
    );
};

export { ShoppingCartSummary };
